import { useClose } from "@headlessui/react";
import { FeatureFlagGate } from "@utils/FeatureFlags/FeatureFlagGate";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";

import { NavItem } from "@fronterahealth/frontera-ui-components";

import { MenuItem } from "..";

interface MenuItemRendererProps {
  item: MenuItem;
  isSelfServeUser: boolean;
  openSubmenus: Set<string>;
  handleItemClick: (name: string) => void;
}

// Flags that gate child items inside a parent submenu (e.g. items under "Video Tool").
// Keyed by path rather than display name — names are just copy and can collide
// (see PROA-1953, where the admin Analytics item and this "Video Analytics" child
// were both named "Analytics" and got cross-gated by the wrong flag) or change
// without warning; a route path is unique and stable.
const childFeatureFlagMap: Partial<Record<MenuItem["path"], keyof FeatureFlags>> = {
  "/session-analyzer": "session-analyzer",
  "/video-sessions": "video-tooling",
  "/video-sessions-v2": "treatment-plan-upload",
  "/analytics": "analytic-dashboard-ui",
};

// Flags that gate top-level nav items, keyed by path for the same reason as
// childFeatureFlagMap above. The admin Analytics item ("/admin-analytics") isn't
// listed here — it's already gated by "portal-admin-analytics" in layouts/index.tsx.
const parentFeatureFlagMap: Partial<Record<MenuItem["path"], keyof FeatureFlags>> = {
  "/session-analyzer": "session-analyzer",
  "/video-sessions": "video-tooling",
  "/video-sessions-v2": "treatment-plan-upload",
};

export const MenuItemRenderer: React.FC<MenuItemRendererProps> = ({
  item,
  isSelfServeUser,
  openSubmenus,
  handleItemClick,
}) => {
  const { pathname } = useLocation();
  const flags = useFlags();
  // Closes the enclosing nav overlay (desktop Popover / mobile Dialog) on selection.
  // No-ops when rendered outside an overlay (persistent settings sidebar).
  const close = useClose();
  const hasChildren = !!item.children?.length;
  const isOpen = openSubmenus.has(item.name);

  // Handle restricted visibility
  if (item.name === "Get Started" && !isSelfServeUser) return null;

  // Filter children based on feature flags
  const filteredChildren =
    item.children?.filter((child) => {
      const flagKey = childFeatureFlagMap[child.path];
      if (flagKey) {
        return flags[flagKey]; // only include if flag is enabled
      }
      return true; // no flag attached, always include
    }) ?? [];

  const active =
    pathname === item.path ||
    (!hasChildren && pathname.startsWith(item.path + "/")) ||
    (hasChildren && pathname.split("/")[1] === item.path.split("/")[1]) ||
    filteredChildren.some((child) => pathname.startsWith(child.path));

  // if item has children AND after filtering there are no children left => skip rendering parent item
  if (hasChildren && filteredChildren.length === 0) {
    return null;
  }

  const navItem = (
    <NavItem
      Icon={item.Icon}
      name={item.name}
      active={active}
      href="#"
      hasChildren={hasChildren}
      isOpen={isOpen}
      onItemClick={() => handleItemClick(item.name)}
    />
  );

  const childMenu =
    hasChildren && isOpen ? (
      <div className="relative ml-4 space-y-1">
        <div className="absolute left-0 -top-4 h-full border-l border-gray-300" />
        <div className="flex flex-col pl-4 gap-2">
          {filteredChildren.map((child) => {
            const isChildActive = pathname.startsWith(child.path);
            return (
              <Link to={child.path} key={child.path} onClick={() => close()}>
                <NavItem iconOnly={false} name={child.name} href="#" active={isChildActive} />
              </Link>
            );
          })}
        </div>
      </div>
    ) : null;

  const link = item.children ? (
    navItem
  ) : (
    <Link
      to={item.path}
      reloadDocument={item.name === "Get Started" && pathname.includes("/assessment-reports")}
      key={item.path}
      onClick={() => close()}
    >
      {navItem}
    </Link>
  );

  const content = (
    <>
      {link}
      {childMenu}
    </>
  );

  // A submenu parent (e.g. "Video Tool") reuses its first child's path for active-state
  // highlighting, so it can collide with that child's entry in the map above. Its
  // children are already filtered individually, so skip parent-level gating here —
  // otherwise turning off the shared path's flag would wrongly hide every sibling.
  const parentFlagKey = !hasChildren ? parentFeatureFlagMap[item.path] : undefined;
  if (parentFlagKey) {
    return (
      <FeatureFlagGate key={item.name} flagKey={parentFlagKey}>
        {content}
      </FeatureFlagGate>
    );
  }

  return <Fragment key={item.name}>{content}</Fragment>;
};
